import Box from '@app/components/common/Box';
import { IFilterParams } from '@app/components/common/MainScreenWithFilterContainer/MainScreenWithFilterContainer';
import Table from '@app/components/common/Table';
import Typography from '@app/components/common/Typography';
import { REPORTS_SCREEN_BY_BUCKET_NAME } from '@app/constants';
import { getPathWithOrgData } from '@app/helpers/navigation';
import useRootSelector from '@app/hooks/useRootSelector';
import { deleteReportWeb } from '@app/state/reports/reportsWebActions';
import { bindActionCreators } from '@reduxjs/toolkit';
import { FilterFns, Row } from '@tanstack/react-table';
import {
  actionCreatorsApp,
  EReportsType,
  filter,
  ICustomReport,
  IReportsStore,
  reportsSelectors,
  TReportRow,
  useSort,
} from '@westondev/tableturn-core';
import { useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { columnHelper, CUSTOM_REPORTS_ROW_ID_PREFIX } from './types';
import Button from '@app/components/common/Button';
import Icon from '@app/components/common/Icon';
import { itemsTableNameStyles } from '../../../ItemComboSales/ItemComboSalesList/ItemComboSalesTable/styles';

type ICustomReportsTable = WithTranslation & {
  bucket?: keyof IReportsStore | 'favoriteReports';
  filterParams: IFilterParams;
};

const CustomReportsTable = ({
  filterParams,
  bucket = 'customReports',
  t,
}: ICustomReportsTable) => {
  const dispatch = useDispatch();
  const deleteReport = bindActionCreators(deleteReportWeb, dispatch);
  const { setShowConfirmationModal, showToast } = bindActionCreators(
    actionCreatorsApp,
    dispatch,
  );
  const onSort = useSort<TReportRow<ICustomReport>>('title');

  const navigate = useNavigate();

  const isFavoriteList = bucket === 'favoriteReports';

  const isMasterMode = useRootSelector(
    state => state.reports.selectedMode.type === EReportsType.MASTER,
  );

  const showDeleteDisabled = () => {
    showToast({
      type: 'info',
      title: t('menuScreen.subcategoryMain.disabledActionToast.title'),
      description: t('menuScreen.deleteToast', {
        bucket: t('loggedIn.sideBar.reports.reports'),
      }),
    });
  };

  const reports = useSelector(
    reportsSelectors.selectCustomReportsArray,
  ) as TReportRow<ICustomReport>[];

  const reportsFiltered = useMemo(
    () =>
      filter<TReportRow<ICustomReport>>(
        reports,
        { ...filterParams } as {
          [key: string]:
            | number
            | string
            | {
                [key: string]: number | string;
              }
            | number[]
            | string[];
        },
        null,
        onSort,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterParams, reports],
  );

  const customFilterFn = (row: Row<TReportRow<ICustomReport>>) => {
    return (
      row.depth > 0 ||
      reportsFiltered.findIndex(item => item.id === row.original.id) !== -1
    );
  };

  const COLUMN_VALUES = useMemo(
    () => {
      const _columnValues = [
        columnHelper.accessor('title', {
          header: t('menuScreen.itemsMain.table.headers.name'),
          filterFn: 'customFilterFn' as keyof FilterFns,
          cell: info => {
            const isSubRow = info.row.depth !== 0;
            const itemId = info.row.original.bucketId;
            const pathUrl = getPathWithOrgData(
              `${
                REPORTS_SCREEN_BY_BUCKET_NAME[info.row.original.bucket]
              }/${itemId}`,
            );
            return (
              <Box
                csx={[
                  itemsTableNameStyles(info.row.original.isActive),
                  { flexDirection: 'row' },
                ]}
                onClick={() => {
                  navigate(`${pathUrl}?isFavorite=${isFavoriteList}`);
                }}>
                <Box className="dot color" />

                <Typography
                  csx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    flex: 1,
                  }}
                  color={!isSubRow ? 'semanticBlue' : undefined}>
                  {info.getValue()}
                </Typography>
              </Box>
            );
          },
          size: 300,
        }),
        columnHelper.accessor('time', {
          header: 'Time',
          cell: info => <Typography>{info.getValue()}</Typography>,
          minSize: 150,
        }),
        columnHelper.accessor('bucketId', {
          header: t('commonTexts.actions'),
          cell: info => {
            const id = info.getValue();
            const bucketUrl =
              REPORTS_SCREEN_BY_BUCKET_NAME[info.row.original.bucket];
            return (
              <Box
                csx={{
                  display: 'flex',
                  gap: '10px',
                }}>
                <Button
                  color="light"
                  onClick={() =>
                    navigate(
                      `${getPathWithOrgData(
                        `${bucketUrl}/edit/${id}`,
                      )}?isFavorite=${isFavoriteList}`,
                    )
                  }
                  variant="secondary"
                  csx={{ width: '50px' }}
                  icon={<Icon name="MdEdit" />}
                />
                <Button
                  color="danger"
                  disabled={!isMasterMode}
                  onClickDisabled={showDeleteDisabled}
                  onClick={() =>
                    setShowConfirmationModal({
                      active: true,
                      title: t('loggedIn.sideBar.reports.report'),
                      type: 'delete',
                      name: info.row.original.title,
                      onSuccess: () => {
                        deleteReport(id, info.row.original.bucket);
                      },
                    })
                  }
                  variant="danger"
                  csx={{ width: '50px' }}
                  icon={<Icon name="MdDeleteForever" />}
                />
              </Box>
            );
          },
          size: 140,
        }),
      ];
      return _columnValues;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bucket, isMasterMode, t],
  );

  const tableFilters = useMemo(
    () =>
      Object.entries(filterParams || {})
        .filter(([key]) =>
          COLUMN_VALUES.some(column => column.accessorKey === key),
        )
        .map(([key, filterParam]) => {
          return {
            id: key,
            value: filterParam,
          };
        }),
    [filterParams, COLUMN_VALUES],
  );

  return (
    <Box
      csx={{
        padding: '15px',
        height: '100%',
      }}>
      <Table
        scrollEnabled
        filterFns={{ customFilterFn }}
        columnFilters={tableFilters}
        columnSorting={[{ id: 'title', desc: false }]}
        manualSorting={false}
        // alwaysShowSortIcon
        enableAlphabeticalSorting
        nestedScrollEnabled
        data={reports}
        columns={COLUMN_VALUES as any}
        cellCsx={{
          height: '70px',
          paddingTop: '5px',
          paddingBottom: '5px',
        }}
        rowIdPrefix={CUSTOM_REPORTS_ROW_ID_PREFIX}
        renderEmptyValues
      />
    </Box>
  );
};

export default CustomReportsTable;
